/**
 * Mobile Fuel — Vehicle / Equipment lookup.
 * Search-first, tap a row to see details modal.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, Cog, Search, X } from 'lucide-react';
import { apiGet } from '../../lib/api';

export default function MobileFuelVehicles() {
  const [tab, setTab] = useState('vehicles');
  const [vehicles, setVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiGet('/fuel/vehicles').then((d) => setVehicles(d?.items || [])).catch(() => {}),
      apiGet('/fuel/equipment').then((d) => setEquipment(d?.items || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const items = tab === 'vehicles' ? vehicles : equipment;
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => {
      const hay = [
        i.vehicle_number, i.vehicle_type, i.driver_name,
        i.equipment_id, i.equipment_type, i.operator_name,
        i.project_name, i.fuel_type,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(s);
    });
  }, [items, q]);

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="m-fuel-vehicles">
      <div className="bg-[#1F7A6E] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m/fuel" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div>
          <div className="font-display font-bold flex items-center gap-1.5"><Truck size={14} /> Fleet Lookup</div>
          <div className="text-[10px] text-slate-300">Vehicles &amp; Equipment</div>
        </div>
      </div>

      <div className="px-4 pt-3 flex gap-1.5" data-testid="m-fuel-vehicles-tabs">
        {['vehicles', 'equipment'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`m-fuel-tab-${t}`}
            className={`flex-1 px-3 py-2 rounded-sm text-xs font-display font-bold uppercase tracking-widest border ${
              tab === t ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-300'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="px-4 pt-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${tab}…`}
            className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white"
            data-testid="m-fuel-search"
          />
        </div>
      </div>

      <div className="p-4 space-y-2">
        {loading ? (
          <div className="text-center text-slate-500 text-sm py-6">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-12">No matches</div>
        ) : filtered.map((row) => (
          <button
            key={row.id}
            onClick={() => setSelected(row)}
            data-testid={`m-fuel-row-${row.id}`}
            className="w-full text-left bg-white border border-slate-200 rounded-xl p-3 active:scale-[0.98] transition"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm text-slate-900 truncate">
                  {row.vehicle_number || row.equipment_id || '—'}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {row.vehicle_type || row.equipment_type || '—'} · {row.fuel_type || '—'}
                </div>
                <div className="text-[10px] text-slate-500 truncate mt-0.5">
                  {row.driver_name || row.operator_name || 'No operator'} · {row.project_name || '—'}
                </div>
              </div>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-display font-bold uppercase tracking-widest ${
                row.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
              }`}>{row.status || '—'}</span>
            </div>
          </button>
        ))}
      </div>

      {selected && <DetailSheet row={selected} kind={tab} onClose={() => setSelected(null)} />}
    </div>
  );
}

function DetailSheet({ row, kind, onClose }) {
  const isVehicle = kind === 'vehicles';
  const rows = isVehicle ? [
    ['Vehicle #', row.vehicle_number],
    ['Type', row.vehicle_type],
    ['Ownership', `${row.ownership || '—'}${row.hire_vendor ? ` · ${row.hire_vendor}` : ''}`],
    ['Fuel', row.fuel_type],
    ['Tank Capacity', `${row.tank_capacity_l || 0} L`],
    ['Mileage Benchmark', `${row.mileage_benchmark_kmpl || 0} km/L`],
    ['Driver', row.driver_name || '—'],
    ['Project', row.project_name || '—'],
    ['GPS Device', row.gps_device_id || '—'],
    ['Fuel Card', row.fuel_card || '—'],
    ['Insurance Expiry', row.insurance_expiry || '—'],
    ['PUC Expiry', row.puc_expiry || '—'],
  ] : [
    ['Equipment ID', row.equipment_id],
    ['Type', row.equipment_type],
    ['Make/Model', row.make_model || '—'],
    ['Ownership', `${row.ownership || '—'}${row.hire_vendor ? ` · ${row.hire_vendor}` : ''}`],
    ['Fuel', row.fuel_type],
    ['Tank Capacity', `${row.tank_capacity_l || 0} L`],
    ['Fuel Benchmark', `${row.fuel_benchmark_lph || 0} L/h`],
    ['Hour Meter', row.hour_meter || 0],
    ['Operator', row.operator_name || '—'],
    ['Project', row.project_name || '—'],
    ['IoT Device', row.iot_device_id || '—'],
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end" data-testid="m-fuel-detail">
      <div className="w-full bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-base flex items-center gap-1.5">
              {isVehicle ? <Truck size={16} /> : <Cog size={16} />} {row.vehicle_number || row.equipment_id}
            </div>
            <div className="text-[11px] text-slate-500">{row.vehicle_type || row.equipment_type}</div>
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1.5 text-slate-500" data-testid="m-fuel-detail-close"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs py-1.5 border-b border-slate-100">
              <span className="text-slate-500 uppercase tracking-widest font-display text-[10px]">{k}</span>
              <span className="font-display font-bold text-slate-800 text-right truncate ml-2">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
